import { onThemeChange, token } from '../../theme';

/**
 * Waterfall — sliding spectrogram (newest row on top, scrolling down).
 *
 * Two stacked canvases:
 *   - heat:    bitmap waterfall, shifted via getImageData/putImageData each frame
 *   - markers: F0..F3 dashed vertical lines + labels (DPR-sharp, redrawn cheaply)
 *
 * Both consume CSS tokens, so theme changes re-color future rows automatically.
 */
export class Waterfall {
  readonly el: HTMLDivElement;
  private heatCanvas: HTMLCanvasElement;
  private heatCtx: CanvasRenderingContext2D;
  private markerCanvas: HTMLCanvasElement;
  private markerCtx: CanvasRenderingContext2D;

  private analyser: AnalyserNode | null = null;
  private data: Uint8Array<ArrayBuffer> | null = null;
  private sampleRate = 48000;
  private maxFreq = 6000;
  freqs: number[] = [1200, 1600, 2000, 2400];

  private animId: number | null = null;
  private ro: ResizeObserver | null = null;
  private offTheme: (() => void) | null = null;

  // Heat canvas: 1 actual pixel = 1 CSS pixel (no DPR) — fillRect-rate dominates,
  // and the visual outcome is identical after CSS scales to display.
  private hw = 0;
  private hh = 0;
  // Marker canvas: DPR-aware so dashes stay crisp.
  private mw = 0;
  private mh = 0;

  // Cached colors (theme-aware)
  private colCold: [number, number, number, number] = [0, 30, 60, 0];
  private colWarm: [number, number, number, number] = [0, 217, 255, 140];
  private colHot: [number, number, number, number] = [255, 176, 0, 240];
  private colBorder = '#1a2332';
  private traceColors: string[] = ['#00d9ff', '#a3ff00', '#ffb000', '#ff3a8c'];

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'waterfall';

    this.heatCanvas = document.createElement('canvas');
    this.heatCanvas.className = 'waterfall__heat';
    const hctx = this.heatCanvas.getContext('2d', { willReadFrequently: true });
    if (!hctx) throw new Error('Waterfall: 2D context unavailable');
    this.heatCtx = hctx;

    this.markerCanvas = document.createElement('canvas');
    this.markerCanvas.className = 'waterfall__markers';
    const mctx = this.markerCanvas.getContext('2d');
    if (!mctx) throw new Error('Waterfall: marker 2D context unavailable');
    this.markerCtx = mctx;

    this.el.appendChild(this.heatCanvas);
    this.el.appendChild(this.markerCanvas);

    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(() => this.fit());
      this.ro.observe(this.el);
    } else {
      window.addEventListener('resize', () => this.fit());
    }
    this.offTheme = onThemeChange(() => this.cacheColors());
    this.cacheColors();
  }

  bind(analyser: AnalyserNode | null, sampleRate?: number): void {
    this.analyser = analyser;
    if (analyser) this.data = new Uint8Array(analyser.frequencyBinCount);
    if (sampleRate) this.sampleRate = sampleRate;
  }

  start(): void {
    if (this.animId !== null) return;
    this.fit();
    const tick = () => {
      this.draw();
      this.animId = requestAnimationFrame(tick);
    };
    this.animId = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.animId !== null) cancelAnimationFrame(this.animId);
    this.animId = null;
  }

  destroy(): void {
    this.stop();
    this.ro?.disconnect();
    this.ro = null;
    this.offTheme?.();
    this.offTheme = null;
  }

  private fit(): void {
    const rect = this.el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;

    // Heat — non-DPR, because we manipulate raw pixels each frame
    const newW = Math.max(1, Math.floor(rect.width));
    const newH = Math.max(1, Math.floor(rect.height));
    if (newW !== this.hw || newH !== this.hh) {
      this.hw = newW;
      this.hh = newH;
      this.heatCanvas.width = newW;
      this.heatCanvas.height = newH;
      // Heat canvas is left transparent so the .waterfall element's CSS
      // background (--waterfall-bg) shows through and tracks theme changes
      // automatically.
    }

    // Markers — DPR-aware
    this.mw = rect.width;
    this.mh = rect.height;
    this.markerCanvas.width = Math.floor(rect.width * dpr);
    this.markerCanvas.height = Math.floor(rect.height * dpr);
    this.markerCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.drawMarkers();
  }

  private cacheColors(): void {
    this.colCold = parseColor(token('waterfall-cold', 'rgba(0,30,60,0)'), [0, 30, 60, 0]);
    this.colWarm = parseColor(token('waterfall-warm', 'rgba(0,217,255,0.55)'), [0, 217, 255, 140]);
    this.colHot = parseColor(token('waterfall-hot', 'rgba(255,176,0,0.95)'), [255, 176, 0, 240]);
    this.colBorder = token('border-hairline', '#1a2332');
    this.traceColors = [
      token('trace-f0', '#00d9ff'),
      token('trace-f1', '#a3ff00'),
      token('trace-f2', '#ffb000'),
      token('trace-f3', '#ff3a8c'),
    ];
    // Repaint markers immediately
    if (this.mw > 0) this.drawMarkers();
  }

  private draw(): void {
    if (this.hw === 0 || this.hh === 0) return;

    if (!this.analyser || !this.data) {
      // Idle: keep ambient bg
      return;
    }

    this.analyser.getByteFrequencyData(this.data);

    // Shift previous content down by 1 pixel
    if (this.hh > 1) {
      const img = this.heatCtx.getImageData(0, 0, this.hw, this.hh - 1);
      this.heatCtx.putImageData(img, 0, 1);
    }

    // Paint new top row
    const row = this.heatCtx.createImageData(this.hw, 1);
    const buf = row.data;
    const binsTotal = this.analyser.frequencyBinCount;
    const hzPerBin = this.sampleRate / this.analyser.fftSize;
    const maxBin = Math.min(
      binsTotal - 1,
      Math.floor(this.maxFreq / hzPerBin),
    );

    for (let x = 0; x < this.hw; x++) {
      // Map pixel x -> bin index (linear, since this is what Spectrum shows above)
      const t = x / this.hw;
      const bin = Math.min(maxBin, Math.max(0, Math.round(t * maxBin)));
      const m = this.data[bin] / 255;
      const [r, g, b, a] = this.heatColor(m);
      const o = x * 4;
      buf[o] = r;
      buf[o + 1] = g;
      buf[o + 2] = b;
      buf[o + 3] = a;
    }
    this.heatCtx.putImageData(row, 0, 0);
  }

  private drawMarkers(): void {
    const { markerCtx: ctx, mw: w, mh: h, freqs, maxFreq } = this;
    if (w === 0 || h === 0) return;
    ctx.clearRect(0, 0, w, h);

    // Marker lines + labels
    ctx.font = `bold 10px ${token('font-mono', 'monospace')}`;
    ctx.textBaseline = 'top';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 4]);

    for (let i = 0; i < freqs.length; i++) {
      const x = (freqs[i] / maxFreq) * w;
      const c = this.traceColors[i] ?? '#fff';
      ctx.strokeStyle = c;
      ctx.globalAlpha = 0.55;
      ctx.beginPath();
      ctx.moveTo(Math.round(x) + 0.5, 0);
      ctx.lineTo(Math.round(x) + 0.5, h);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    ctx.setLineDash([]);

    // Border
    ctx.strokeStyle = this.colBorder;
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);

    // Corner annotation
    ctx.font = `10px ${token('font-mono', 'monospace')}`;
    ctx.fillStyle = token('text-mute', '#3e4858');
    ctx.textAlign = 'left';
    ctx.fillText('TIME ↓', 6, 4);
  }

  private heatColor(m: number): [number, number, number, number] {
    // 3-stop gradient: cold (transparent) → warm → hot.
    // m below ~0.08 stays at near-transparent to keep noise quiet.
    if (m <= 0.04) return this.colCold;
    if (m < 0.5) {
      const t = (m - 0.04) / (0.5 - 0.04);
      return lerpRgba(this.colCold, this.colWarm, t);
    }
    const t = Math.min(1, (m - 0.5) / 0.5);
    return lerpRgba(this.colWarm, this.colHot, t);
  }
}

function lerpRgba(
  a: [number, number, number, number],
  b: [number, number, number, number],
  t: number,
): [number, number, number, number] {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
    Math.round(a[3] + (b[3] - a[3]) * t),
  ];
}

/** Parse hex (#rgb / #rrggbb) or rgb()/rgba() into [r,g,b,a] with a in 0..255. */
function parseColor(
  src: string,
  fallback: [number, number, number, number],
): [number, number, number, number] {
  const s = src.trim();
  if (!s) return fallback;
  if (s.startsWith('#')) {
    const hex = s.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return [r, g, b, 255];
    }
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return [r, g, b, 255];
    }
    return fallback;
  }
  const m = s.match(/rgba?\(([^)]+)\)/);
  if (!m) return fallback;
  const parts = m[1].split(',').map((p) => p.trim());
  const r = parseInt(parts[0], 10);
  const g = parseInt(parts[1], 10);
  const b = parseInt(parts[2], 10);
  const a = parts[3] !== undefined ? Math.round(parseFloat(parts[3]) * 255) : 255;
  if (![r, g, b].every(Number.isFinite)) return fallback;
  return [r, g, b, Number.isFinite(a) ? a : 255];
}
